import { useState } from "react";
import { FanBadge } from "@/components/fanworks/FanBadge";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Search, Star, BookOpen, Pin, PinOff, Sparkles, BookMarked } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { fmtNum } from "@/lib/studio";
import { coverUrl } from "@/components/studio/BookDetails";
import { useAuth } from "@/context/AuthContext";
import { toggleFeatured } from "@/components/admin/FeaturedPanel";

const Cover = ({ s, w = "w-24" }) => (
  <div className={`flex aspect-[2/3] ${w} shrink-0 items-center justify-center overflow-hidden rounded bg-[#1A202C]`}>
    {s.has_cover ? <img src={coverUrl(s)} alt="" className="h-full w-full object-cover" /> : <BookOpen className="h-6 w-6 text-[#A8A090]" />}
  </div>
);

const ProgressBar = ({ p, id }) => (
  <div className="mt-2" data-testid={`progress-${id}`}>
    <div className="h-1 w-full overflow-hidden rounded bg-[#2D3442]"><div className="h-full bg-[#F59E0B]" style={{ width: `${p.percent}%` }} /></div>
    <div className="mt-1 text-[11px] text-[#A8A090]">Chapter {p.chapter_number} of {p.chapter_count} · {p.percent}%</div>
  </div>
);

const Card = ({ s, progress, featured, isAdmin, onPin }) => (
  <div className={`group relative flex gap-4 rounded-md border p-4 transition-colors hover:border-[#D97706]/60 ${featured ? "border-[#D97706]/50 bg-[#1A1508]" : "border-[#2D3442] bg-[#13171F]"}`} data-testid={`library-card-${s.id}`}>
    <Link to={`/read/${s.id}`} className="absolute inset-0" aria-label={s.title} data-testid={`library-open-${s.id}`} />
    <Cover s={s} />
    <div className="min-w-0 flex-1">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.16em] text-[#A8A090]">{featured && <span className="mr-2 text-[#F59E0B]" data-testid={`featured-badge-${s.id}`}>Featured ·</span>}{s.universe_name}</div>
        {isAdmin && <button onClick={() => onPin(s.id, !featured)} title={featured ? "Unpin from Featured" : "Pin to Featured"} className="relative z-10 text-[#A8A090] hover:text-[#F59E0B]" data-testid={`library-pin-${s.id}`}>{featured ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}</button>}
      </div>
      <div className="mt-1 font-display text-2xl leading-tight text-[#F3EBDD] group-hover:text-[#F59E0B] [overflow-wrap:anywhere]">{s.title}</div>
      {s.author && <div className="text-xs text-[#A8A090]">by {s.author}</div>}
      <FanBadge story={s} className="mt-1" />
      {(s.subtitle || s.synopsis) && <p className="mt-2 line-clamp-2 text-sm text-[#A8A090]">{s.subtitle || s.synopsis}</p>}
      <div className="mt-2 flex items-center gap-3 text-xs text-[#A8A090]">
        <span>{s.chapters} chapters · {fmtNum(s.words)} words</span>
        {s.rating && <span className="flex items-center gap-1 text-[#FBBF24]" data-testid={`library-rating-${s.id}`}><Star className="h-3 w-3 fill-current" />{s.rating} ({s.ratings})</span>}
      </div>
      {progress && <>
        <ProgressBar p={progress} id={s.id} />
        <Link to={`/read/${s.id}?c=${progress.chapter_id}`} className="relative z-10 mt-2 inline-flex items-center gap-1 rounded-full bg-[#D97706] px-3 py-1 text-xs font-medium text-[#0B0D10] hover:bg-[#F59E0B]" data-testid={`library-continue-${s.id}`}><BookMarked className="h-3.5 w-3.5" />Continue reading</Link>
      </>}
    </div>
  </div>
);

const ContinueRow = ({ items }) => (
  <section className="mt-10" data-testid="continue-row">
    <div className="lf-label mb-3 flex items-center gap-2"><BookMarked className="h-3.5 w-3.5" />Continue reading</div>
    <div className="flex gap-4 overflow-x-auto pb-2">
      {items.map((s) => (
        <Link key={s.id} to={`/read/${s.id}?c=${s.progress.chapter_id}`} className="flex w-72 shrink-0 gap-3 rounded-md border border-[#2D3442] bg-[#13171F] p-3 transition-colors hover:border-[#D97706]/60" data-testid={`continue-card-${s.id}`}>
          <Cover s={s} w="w-14" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-display text-lg text-[#F3EBDD]">{s.title}</div>
            <div className="truncate text-[11px] text-[#A8A090]">{s.universe_name}</div>
            <ProgressBar p={s.progress} id={`row-${s.id}`} />
          </div>
        </Link>
      ))}
    </div>
  </section>
);

export default function LibraryPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = !!user?.plan?.is_admin;
  const [q, setQ] = useState("");
  const { data = [], isLoading } = useQuery({ queryKey: ["library", q], queryFn: () => api.get("/library", { params: { q } }).then((r) => r.data) });
  const { data: featured = [] } = useQuery({ queryKey: ["library-featured"], queryFn: () => api.get("/library/featured").then((r) => r.data) });
  const { data: cont = [] } = useQuery({ queryKey: ["library-continue"], queryFn: () => api.get("/library/continue").then((r) => r.data) });
  const progress = Object.fromEntries(cont.map((s) => [s.id, s.progress]));
  const featIds = new Set(featured.map((s) => s.id));
  const pin = (sid, on) => toggleFeatured(sid, on, qc);
  const rest = q.trim() ? data : data.filter((s) => !featIds.has(s.id));
  const card = (s) => <Card key={s.id} s={s} progress={progress[s.id]} featured={featIds.has(s.id)} isAdmin={isAdmin} onPin={pin} />;
  return (
    <div className="min-h-screen lf-glow lf-grain">
      <div className="relative z-10 mx-auto max-w-6xl px-6 py-10 sm:px-10" data-testid="library-page">
        <Link to="/" className="flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid="library-back"><ChevronLeft className="h-3.5 w-3.5" />Your universes</Link>
        <div className="lf-label mb-3 mt-10">More Universes</div>
        <h1 className="font-display text-4xl text-[#F3EBDD] sm:text-5xl lg:text-6xl">Stories from other worlds.</h1>
        <p className="mt-3 max-w-xl text-sm text-[#A8A090]">Read stories LoreRight writers have chosen to share. Leave a rating or a comment on any chapter to help them improve.</p>
        {cont.length > 0 && <ContinueRow items={cont} />}
        {featured.length > 0 && !q.trim() && (
          <section className="mt-10" data-testid="featured-section">
            <div className="lf-label mb-3 flex items-center gap-2 text-[#F59E0B]"><Sparkles className="h-3.5 w-3.5" />Featured</div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">{featured.map(card)}</div>
          </section>
        )}
        <div className="relative mt-10 max-w-md"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A8A090]" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title" className="pl-9" data-testid="library-search" /></div>
        {!isLoading && data.length === 0 && <p className="mt-10 text-sm text-[#A8A090]" data-testid="library-empty">No shared stories yet.</p>}
        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">{rest.map(card)}</div>
      </div>
    </div>
  );
}
